import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAPI } from '../hooks/useAPI';
import { contentAPI, contactAPI } from '../services/api';

const Contact = () => {
  const { data: content, loading, error } = useAPI(contentAPI.getContactContent, []);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    country: '',
    company: '',
    inquiryType: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await contactAPI.submitContactForm(formData);
      setSubmitStatus({ type: 'success', message: response.message || 'Thank you for contacting us! We will get back to you soon.' });
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        country: '',
        company: '',
        inquiryType: '',
        message: ''
      });
    } catch (err) {
      setSubmitStatus({ type: 'error', message: 'Failed to submit form. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header variant="light" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-900 mx-auto mb-4"></div>
            <p className="text-xl text-gray-700">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header variant="light" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Content</h2>
            <p className="text-gray-700">{error}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div>
        <section className="overflow-hidden">
          <Header variant="light" />
        </section>
      </div>

      <section className="py-12 lg:py-24 relative overflow-hidden bg-gradient-to-b from-teal-50 to-white">
        <div className="container mx-auto px-4 relative">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-20">
              <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl tracking-sm mb-6 text-teal-900">{content?.hero?.title || 'Get in Touch'}</h1>
              <p className="text-lg text-gray-700 mb-3">
                {content?.hero?.subtitle || 'Have questions about our export services? We\'d love to hear from you.'}
              </p>
              <p className="text-gray-600">Fill out the form below and our team will respond within 24 hours.</p>
              <p className="text-gray-600 mt-4">
                {content?.directContact?.text || 'You can also contact us directly at'}{' '}
                <a href={`mailto:${content?.directContact?.email}`} className="text-teal-900 font-semibold hover:underline">
                  {content?.directContact?.email || 'info@islandbridgeexports.com'}
                </a>
              </p>
            </div>
            <div className="p-8 bg-white rounded-2xl shadow-lg border border-gray-200">
              {submitStatus && (
                <div className={`mb-6 p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {submitStatus.message}
                </div>
              )}
              <div>
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block pl-4 mb-2 text-sm font-medium text-gray-700" htmlFor="fullName">
                        {content?.formLabels?.fullName || 'Full Name'} <span className="text-red-500">*</span>
                      </label>
                      <input
                        className="w-full px-4 py-3 text-gray-900 outline-none ring-offset-0 focus:ring-2 focus:ring-teal-500 shadow rounded-lg"
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="block pl-4 mb-2 text-sm font-medium text-gray-700" htmlFor="email">
                        {content?.formLabels?.email || 'Email'} <span className="text-red-500">*</span>
                      </label>
                      <input
                        className="w-full px-4 py-3 text-gray-900 outline-none ring-offset-0 focus:ring-2 focus:ring-teal-500 shadow rounded-lg"
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block pl-4 mb-2 text-sm font-medium text-gray-700" htmlFor="country">
                        {content?.formLabels?.country || 'Country'} <span className="text-red-500">*</span>
                      </label>
                      <input
                        className="w-full px-4 py-3 text-gray-900 outline-none ring-offset-0 focus:ring-2 focus:ring-teal-500 shadow rounded-lg"
                        type="text"
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  
                    <div>
                      <label className="block pl-4 mb-2 text-sm font-medium text-gray-700" htmlFor="company">
                        {content?.formLabels?.company || 'Company'} <span className="text-red-500">*</span>
                      </label>
                      <input
                        className="w-full px-4 py-3 text-gray-900 outline-none ring-offset-0 focus:ring-2 focus:ring-teal-500 shadow rounded-lg"
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block pl-4 mb-2 text-sm font-medium text-gray-700" htmlFor="inquiryType">
                      {content?.formLabels?.inquiryType || 'Type of Inquiry'} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative w-full bg-white rounded-lg">
                      <span className="absolute top-1/2 right-0 mr-4 transform -translate-y-1/2 pointer-events-none">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6.3999 8.2L9.9999 11.8L13.5999 8.2" stroke="#646A69" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </span>
                      <select
                        className="w-full px-4 py-3 text-gray-600 border border-gray-300 rounded-lg appearance-none cursor-pointer outline-none ring-offset-0 focus:ring-2 focus:ring-teal-500 bg-white"
                        id="inquiryType"
                        name="inquiryType"
                        value={formData.inquiryType}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select inquiry type</option>
                        {content?.inquiryTypes?.map((type, index) => (
                          <option key={index} value={type.toLowerCase().replace(/\s+/g, '-')}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block pl-4 mb-2 text-sm font-medium text-gray-700" htmlFor="message">
                      {content?.formLabels?.message || 'Message'} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className="w-full px-4 py-3 text-gray-900 outline-none ring-offset-0 focus:ring-2 focus:ring-teal-500 shadow rounded-lg resize-none"
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Provide details about your request..."
                      required
                    ></textarea>
                  </div>

                  <button 
                    type="submit"
                    disabled={submitting}
                    className="flex w-full py-3 px-5 items-center justify-center font-medium text-white hover:text-teal-900 border border-teal-900 hover:border-lime-500 bg-teal-900 hover:bg-lime-500 rounded-full transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="mr-2">{submitting ? 'Submitting...' : (content?.formLabels?.submitButton || 'Send Message')}</span>
                    {!submitting && (
                      <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.25 10H15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M10.5 4.75L15.75 10L10.5 15.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                      </svg>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Contact;
